import EventBusyIcon from '@mui/icons-material/EventBusy';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import UsbOffIcon from '@mui/icons-material/UsbOff';
import { Box, Button, SvgIconProps, Typography } from '@mui/material';
import {
  GilAttentionItem,
  GilAttentionTypeEnum,
  GilDataStatus,
  GilProgressMetrics,
  GilTelemetryStatusEnum,
  GilWeeklyPoint,
} from '@protos/learning_management/lms.assessment.student.apis_pb';
import type { ApexOptions } from 'apexcharts';
import { ComponentType, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';
import { InsightTile } from './CodingInsights';
import { GIL, gilSize, gilText } from './gilTheme';
import { GilColumn, GilReportTable } from './GilReportTable';
import { PDF_BLOCK_CLASS } from './reportPdf';
import { SectionCard } from './ReportWidgets';
import {
  formatDate,
  formatDateTime,
  formatDays,
  formatDuration,
  formatHours,
  formatPercent,
  toPercent,
} from './utils';

/**
 * The derived sections of the GIL dashboards: data freshness, pacing and
 * grading, what needs the teacher's attention, and the weekly trend.
 *
 * Like the coding cards, each renders nothing when its data is absent - an
 * empty figure would be a claim the data cannot back.
 */

const PRIMARY = '#007CDC';
/** A lighter step of the same hue, for the unfinished current week. */
const PRIMARY_LIGHT = '#99CBF1';
const INK = GIL.text;
const MUTED = GIL.muted;
const WARNING_INK = '#8A5A00';
const FRICTION_COLOR = '#E6533C';

const styles: IStyles = {
  note: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: gilSize(20, 14),
    rowGap: pxToRem(4),
    boxSizing: 'border-box',
    paddingX: gilSize(16, 12),
    paddingY: gilSize(10, 8),
    border: `1px solid ${GIL.border}`,
    borderRadius: gilSize(10, 8),
    backgroundColor: GIL.surface,
  },
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(3, minmax(0, 1fr))',
    },
    gap: gilSize(16, 10),
  },
  attentionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: gilSize(10, 8),
  },
  attentionRow: {
    display: 'flex',
    // On phones the tag sits above the names so they keep the full width.
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'flex-start', md: 'center' },
    gap: gilSize(14, 8),
    boxSizing: 'border-box',
    border: `1px solid ${GIL.border}`,
    borderRadius: gilSize(10, 8),
    paddingX: gilSize(16, 12),
    paddingY: gilSize(12, 10),
    backgroundColor: '#FFFFFF',
    transition: 'border-color 0.15s',
  },
  attentionTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: gilSize(6, 4),
    flexShrink: 0,
    boxSizing: 'border-box',
    minWidth: { md: pxToRem(124), lg: pxTovW(160) },
    borderRadius: gilSize(6, 5),
    paddingX: gilSize(10, 8),
    paddingY: gilSize(5, 4),
    '& svg': { fontSize: gilSize(18, 16) },
  },
  toggleButton: {
    ...gilText(13, 600, GIL.green),
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: 0,
    paddingX: pxToRem(8),
  },
};

// ---- Data status -------------------------------------------------------------

/**
 * When the report was made and how complete its coding data is. Coding events
 * from a school's lab server arrive when it syncs, so a gap here usually means
 * "not synced yet", and the note says so rather than letting it read as "did
 * no coding".
 */
export const DataStatusNote = ({ status }: { status?: GilDataStatus }) => {
  if (!status) {
    return null;
  }

  const unavailable =
    status.telemetryStatus ===
    GilTelemetryStatusEnum.GIL_TELEMETRY_STATUS_UNAVAILABLE;
  const partial =
    status.telemetryStatus ===
      GilTelemetryStatusEnum.GIL_TELEMETRY_STATUS_AVAILABLE &&
    status.codingSittingCount > 0 &&
    status.codingSittingsWithTelemetryCount < status.codingSittingCount;

  return (
    <Box sx={styles.note} className={PDF_BLOCK_CLASS}>
      <Typography sx={gilText(12, 500, MUTED)}>
        {`Updated ${formatDateTime(status.generatedAt)}`}
      </Typography>
      {status.latestActivityAt && (
        <Typography sx={gilText(12, 500, MUTED)}>
          {`Last lab activity ${formatDate(status.latestActivityAt)}`}
        </Typography>
      )}
      {/* {unavailable && (
        <Typography sx={gilText(12, 600, WARNING_INK)}>
          Coding data could not be loaded right now, so coding figures are
          hidden. Try again shortly.
        </Typography>
      )} */}
      {/* {partial && (
        <Typography sx={gilText(12, 600, WARNING_INK)}>
          {`Coding data received for ${
            status.codingSittingsWithTelemetryCount
          } of ${status.codingSittingCount} coding sessions (${formatPercent(
            status.telemetryCoveragePercent
          )}) - the rest may still be syncing from the school's lab server.`}
        </Typography>
      )} */}
    </Box>
  );
};

// ---- Pacing & grading --------------------------------------------------------

/**
 * Pacing and grading figures. Submissions and grades count students (marks
 * are individual); start and stall figures count groups (a group shares one
 * device).
 */
export const ProgressMetricsCard = ({
  metrics,
  title = 'Pacing & Grading',
  subtitle,
}: {
  metrics?: GilProgressMetrics;
  title?: string;
  subtitle?: string;
}) => {
  if (
    !metrics ||
    (metrics.submittedCount === 0 &&
      metrics.overdueCount === 0 &&
      metrics.stalledGroupCount === 0 &&
      metrics.medianDaysToStart === undefined)
  ) {
    return null;
  }

  return (
    <SectionCard title={title} subtitle={subtitle}>
      <Box sx={styles.metricGrid}>
        <InsightTile
          value={formatPercent(metrics.onTimeCompletionPercent)}
          label="Submitted On Time"
          hint={
            metrics.submittedWithDueDateCount > 0
              ? `Of ${metrics.submittedWithDueDateCount} submitted with a due date`
              : 'No submissions with a due date yet'
          }
        />
        <InsightTile
          value={String(metrics.overdueCount)}
          label="Overdue"
          hint="Students past the due date, not submitted"
          color={metrics.overdueCount > 0 ? FRICTION_COLOR : undefined}
        />
        <InsightTile
          value={String(metrics.stalledGroupCount)}
          label="Stalled Groups"
          hint="Started, but no work for 14+ days"
          color={metrics.stalledGroupCount > 0 ? FRICTION_COLOR : undefined}
        />
        <InsightTile
          value={formatDays(metrics.medianDaysToStart)}
          label="Time to Start"
          hint="Median, from assigning to the group's first session"
        />
        <InsightTile
          value={`${metrics.gradedCount}/${metrics.submittedCount}`}
          label="Submissions Graded"
          hint={
            metrics.remarksPercent === undefined
              ? 'None graded yet'
              : `${formatPercent(metrics.remarksPercent)} with remarks`
          }
        />
        <InsightTile
          value={formatHours(metrics.medianGradingTurnaroundHours)}
          label="Grading Turnaround"
          hint="Median, from submission to review"
        />
      </Box>
    </SectionCard>
  );
};

// ---- Needs attention ---------------------------------------------------------

interface AttentionMeta {
  label: string;
  sinceLabel: string;
  fg: string;
  bg: string;
  Icon: ComponentType<SvgIconProps>;
}

const ATTENTION_META: Record<GilAttentionTypeEnum, AttentionMeta> = {
  [GilAttentionTypeEnum.GIL_ATTENTION_TYPE_UNDEFINED]: {
    label: 'Check',
    sinceLabel: 'Since',
    fg: MUTED,
    bg: '#F4F4F4',
    Icon: HourglassEmptyIcon,
  },
  [GilAttentionTypeEnum.GIL_ATTENTION_TYPE_OVERDUE]: {
    label: 'Overdue',
    sinceLabel: 'Was due',
    fg: '#B3261E',
    bg: '#FDECEA',
    Icon: EventBusyIcon,
  },
  [GilAttentionTypeEnum.GIL_ATTENTION_TYPE_HARDWARE_BLOCKED]: {
    label: 'Kit problem',
    sinceLabel: 'Last worked',
    fg: '#A04000',
    bg: '#FFF1E6',
    Icon: UsbOffIcon,
  },
  [GilAttentionTypeEnum.GIL_ATTENTION_TYPE_STALLED]: {
    label: 'Stalled',
    sinceLabel: 'Last worked',
    fg: WARNING_INK,
    bg: '#FFF9E0',
    Icon: PauseCircleOutlineIcon,
  },
  [GilAttentionTypeEnum.GIL_ATTENTION_TYPE_NOT_STARTED]: {
    label: 'Not started',
    sinceLabel: 'Assigned',
    fg: '#4F4F4F',
    bg: '#F1F1F1',
    Icon: HourglassEmptyIcon,
  },
  [GilAttentionTypeEnum.GIL_ATTENTION_TYPE_UNGRADED]: {
    label: 'To grade',
    sinceLabel: 'Submitted',
    fg: '#005A9E',
    bg: '#EAF4FC',
    Icon: RateReviewOutlinedIcon,
  },
};

const NAMES_SHOWN = 3;

/** "Group Red · Asha, Bharat, Charu +1" */
const attentionWho = (item: GilAttentionItem) => {
  const names = item.students.map((student) => student.studentName);
  const shown = names.slice(0, NAMES_SHOWN).join(', ');
  const rest = names.length - NAMES_SHOWN;
  const people = rest > 0 ? `${shown} +${rest}` : shown;
  return [item.groupName, people].filter(Boolean).join(' · ');
};

const COLLAPSED_COUNT = 8;

/**
 * What the teacher can act on, most urgent first: overdue work, kits that
 * look broken, stalled and unstarted groups, and submissions to grade.
 * Teacher-facing only.
 */
export const NeedsAttentionCard = ({
  items,
  onOpen,
  showProject = true,
  title = 'Needs Your Attention',
}: {
  items?: GilAttentionItem[];
  onOpen?: (item: GilAttentionItem) => void;
  showProject?: boolean;
  title?: string;
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!items) {
    return null;
  }

  const visible = expanded ? items : items.slice(0, COLLAPSED_COUNT);

  return (
    <SectionCard
      title={title}
      subtitle={
        items.length > 0
          ? `${items.length} item${items.length === 1 ? '' : 's'}`
          : undefined
      }
      action={
        items.length > COLLAPSED_COUNT ? (
          <Button
            sx={styles.toggleButton}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : `Show all ${items.length}`}
          </Button>
        ) : undefined
      }
    >
      {items.length === 0 ? (
        <Typography sx={gilText(14, 500, MUTED)}>
          All caught up - nothing overdue, stalled or waiting to be graded.
        </Typography>
      ) : (
        <Box sx={styles.attentionList}>
          {visible.map((item, index) => {
            const meta = ATTENTION_META[item.type];
            const open = onOpen ? () => onOpen(item) : undefined;
            return (
              <Box
                key={`${item.type}-${item.assessmentId}-${
                  item.studentGroupId ?? item.students[0]?.studentId
                }-${index}`}
                sx={{
                  ...styles.attentionRow,
                  cursor: open ? 'pointer' : 'default',
                  '&:hover': open ? { borderColor: GIL.green } : undefined,
                }}
                role={open ? 'button' : undefined}
                tabIndex={open ? 0 : undefined}
                onClick={open}
                onKeyDown={(event) => event.key === 'Enter' && open?.()}
              >
                <Box sx={{ ...styles.attentionTag, backgroundColor: meta.bg }}>
                  <meta.Icon sx={{ color: meta.fg }} />
                  <Typography noWrap sx={gilText(12, 600, meta.fg)}>
                    {meta.label}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    width: { xs: '100%', md: 'auto' },
                  }}
                >
                  <Typography noWrap sx={gilText(14, 600, GIL.text)}>
                    {attentionWho(item)}
                  </Typography>
                  <Typography noWrap sx={gilText(12, 500, MUTED)}>
                    {[
                      showProject ? item.projectTitle : undefined,
                      item.since
                        ? `${meta.sinceLabel} ${formatDate(item.since)}`
                        : undefined,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </SectionCard>
  );
};

// ---- Weekly trend ------------------------------------------------------------

const weekLabel = (point: GilWeeklyPoint) =>
  point.weekStart
    ? point.weekStart
        .toDate()
        .toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    : '';

/**
 * The share of students credited with a working build each week - Weekly
 * Active Builders, the GIL North Star - as one series of columns on one 0-100%
 * axis. When coding data is unavailable the builders figure is unknown, so the
 * chart falls back to the share of students who worked at all, and says so.
 *
 * Every value is also reachable without the chart: the table view lists each
 * week, and the hover tooltip carries the counts behind the percentage.
 */
export const WeeklyTrendCard = ({
  points,
  title = 'Weekly Trend',
}: {
  points?: GilWeeklyPoint[];
  title?: string;
}) => {
  const [asTable, setAsTable] = useState(false);

  const view = useMemo(() => {
    const list = points ?? [];
    const builders = list.some((point) => point.activeBuilders !== undefined);
    const valueOf = (point: GilWeeklyPoint) =>
      builders
        ? point.activeBuildersPercent ?? 0
        : toPercent(point.activeStudents, point.enrolledStudents);
    const lastComplete = list.reduce(
      (found, point, index) => (point.isPartial ? found : index),
      -1
    );
    return { list, builders, valueOf, lastComplete };
  }, [points]);

  if (view.list.length === 0) {
    return null;
  }

  const { list, builders, valueOf, lastComplete } = view;
  const measure = builders
    ? 'Weekly Active Builders'
    : 'Weekly Active Students';
  const latest = lastComplete >= 0 ? list[lastComplete] : undefined;

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
      fontFamily: GIL.font,
    },
    plotOptions: {
      bar: {
        columnWidth: '58%',
        borderRadius: 4,
        borderRadiusApplication: 'end',
        dataLabels: { position: 'top' },
      },
    },
    // One direct label only, on the last complete week; the axis and the
    // tooltip carry the rest.
    dataLabels: {
      enabled: true,
      offsetY: -18,
      formatter: (value, { dataPointIndex }) =>
        dataPointIndex === lastComplete ? `${Math.round(Number(value))}%` : '',
      style: { colors: [INK], fontSize: '12px', fontWeight: 700 },
    },
    grid: {
      borderColor: '#EEEEEE',
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: list.map((point) =>
        point.isPartial ? 'This week' : weekLabel(point)
      ),
      axisBorder: { color: '#E0DFDE' },
      axisTicks: { show: false },
      labels: { style: { colors: MUTED, fontSize: '11px' }, rotate: -45 },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 4,
      labels: {
        formatter: (value) => `${Math.round(value)}%`,
        style: { colors: [MUTED], fontSize: '11px' },
      },
    },
    states: { hover: { filter: { type: 'darken' } } },
    tooltip: {
      custom: ({ dataPointIndex }) => {
        const point = list[dataPointIndex];
        const lines = [
          `<strong>${
            point.isPartial
              ? 'This week (so far)'
              : `Week of ${weekLabel(point)}`
          }</strong>`,
          builders
            ? `${point.activeBuilders ?? 0} of ${
                point.enrolledStudents
              } students built something that ran (${formatPercent(
                point.activeBuildersPercent
              )})`
            : `${point.activeStudents} of ${point.enrolledStudents} students worked on GIL`,
          `${point.activeStudents} students active in ${
            point.activeGroups
          } group${point.activeGroups === 1 ? '' : 's'}`,
          `${formatDuration(point.totalTimeInSec)} in total`,
        ];
        return `<div style="padding:8px 10px;font-size:12px;line-height:1.5;color:${INK}">${lines.join(
          '<br/>'
        )}</div>`;
      },
    },
    legend: { show: false },
  };

  const series = [
    {
      name: measure,
      data: list.map((point) => ({
        x: point.isPartial ? 'This week' : weekLabel(point),
        y: Math.round(valueOf(point) * 10) / 10,
        fillColor: point.isPartial ? PRIMARY_LIGHT : PRIMARY,
      })),
    },
  ];

  const tableColumns: GilColumn<GilWeeklyPoint>[] = [
    {
      key: 'week',
      header: 'Week of',
      align: 'left',
      render: (point) =>
        point.isPartial ? `${weekLabel(point)} (so far)` : weekLabel(point),
    },
    {
      key: 'active',
      header: 'Active Students',
      render: (point) => `${point.activeStudents}/${point.enrolledStudents}`,
    },
    {
      key: 'groups',
      header: 'Groups Working',
      render: (point) => point.activeGroups,
    },
    {
      key: 'builders',
      header: 'Built Something That Ran',
      render: (point) =>
        point.activeBuilders === undefined
          ? '—'
          : `${point.activeBuilders} (${formatPercent(
              point.activeBuildersPercent
            )})`,
    },
    {
      key: 'time',
      header: 'Time',
      render: (point) => formatDuration(point.totalTimeInSec),
    },
  ];

  return (
    <SectionCard
      title={title}
      subtitle={
        latest
          ? `${measure}: ${formatPercent(
              valueOf(latest)
            )} in the week of ${weekLabel(latest)}${
              builders ? '' : ' (coding data unavailable)'
            }`
          : measure
      }
      action={
        <Button sx={styles.toggleButton} onClick={() => setAsTable(!asTable)}>
          {asTable ? 'View as chart' : 'View as table'}
        </Button>
      }
    >
      {asTable ? (
        <GilReportTable
          columns={tableColumns}
          rows={list}
          rowKey={(point) => Number(point.weekStart?.seconds ?? 0)}
        />
      ) : (
        <Box role="img" aria-label={`${measure} by week, as a column chart`}>
          <ReactApexChart
            type="bar"
            height={280}
            options={options}
            series={series}
          />
          <Typography sx={gilText(12, 500, MUTED)}>
            {builders
              ? 'Share of students whose group got a program running in the simulator or on a board that week. The lighter column is the week in progress.'
              : 'Share of students whose group worked on GIL that week. The lighter column is the week in progress.'}
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
};
